// The per-row "Solo prep" badge the My Sessions page renders through the shared
// `SessionList`'s `renderRowActions` slot, beside the role badge. A small,
// non-interactive presentational element marking a one-person worldview-mapping
// session so it reads visually distinct from a two-sided live debate.
//
// Refinement: tasks/refinements/solo_prep/sp_discovery_badge.md
// TaskJuggler: solo_prep.sp_discovery_badge
// ADR:        0049 (a flag, not a role, drives behavior), 0024 (i18n), 0040 (axe).
//
// Solo is an axis ORTHOGONAL to role (a session is solo or not, independently of
// whether the caller is host / moderator / debater), so this is a peer of
// `SessionRoleBadge` rather than an extension of it. Presence-means-solo: the
// badge renders only for solo rows; a live-debate row shows no counter-badge,
// because the live debate is the default and overwhelming common case. Kept
// separate from the route so the boolean → badge mapping is unit-testable in
// isolation.

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export interface SoloSessionBadgeProps {
  /**
   * Whether the row is a solo-prep session. `false`/`undefined` (the common
   * live-debate case, or an unresolved correlation) render nothing.
   */
  readonly solo?: boolean | undefined;
}

export function SoloSessionBadge({ solo }: SoloSessionBadgeProps): ReactElement | null {
  const { t } = useTranslation();

  if (solo !== true) {
    return null;
  }

  return (
    <span
      data-testid="solo-session-badge"
      data-solo="true"
      aria-label={t('discovery.mySessions.solo.ariaLabel')}
      className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-medium text-violet-700"
    >
      {t('discovery.mySessions.solo.label')}
    </span>
  );
}

export default SoloSessionBadge;
